import { useEffect } from 'react';

// App View pull-to-refresh. Pull the page down when it is already at the top; past THRESHOLD, letting go
// calls onRefresh (the layout rebuilds the current page, which re-fetches its data). The page follows the
// finger with resistance and an arrow turns into a spinner. Ignored inside sheets/dialogs, map panels,
// swipe strips and form fields so it never fights their own gestures.
const THRESHOLD = 70;
const MAX = 120;
const SKIP = '[data-sheet],.modal,.modal-overlay,.leaflet-container,.swipe-track,input,textarea,select,[data-no-ptr]';

export default function usePullToRefresh(mainRef, indRef, onRefresh) {
  useEffect(() => {
    const main = mainRef.current;
    const ind = indRef.current;
    if (!main || !ind) return undefined;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    let st = null;
    let busy = false;
    const content = () => main.querySelector('.page-content');

    const paint = (d, animate) => {
      const c = content();
      const tr = animate && !reduce ? 'transform .3s cubic-bezier(.22,.8,.3,1)' : 'none';
      if (c) { c.style.transition = tr; c.style.transform = d ? `translateY(${d}px)` : ''; }
      ind.style.transition = tr;
      ind.style.transform = `translateY(${Math.max(0, d - 44)}px) rotate(${d * 3}deg)`;
      ind.style.opacity = d ? String(Math.min(1, d / THRESHOLD)) : '0';
    };

    const start = (e) => {
      if (busy || e.touches.length !== 1 || main.scrollTop > 0 || e.target.closest?.(SKIP)) return;
      st = { y0: e.touches[0].clientY, x0: e.touches[0].clientX, d: 0, on: false };
    };
    const move = (e) => {
      if (!st) return;
      const dy = e.touches[0].clientY - st.y0;
      const dx = e.touches[0].clientX - st.x0;
      if (!st.on) {
        if (Math.abs(dx) > 10 || dy < -6) { st = null; return; }
        if (dy < 10) return;
        if (main.scrollTop > 0) { st = null; return; }
        st.on = true;
      }
      st.d = Math.min(MAX, dy * 0.5);
      paint(st.d, false);
    };
    const end = () => {
      if (!st) return;
      const { d, on } = st; st = null;
      if (!on) return;
      if (d >= THRESHOLD) {
        busy = true;
        navigator.vibrate?.(10);
        ind.classList.add('av-ptr-spin');
        paint(THRESHOLD * 0.8, true);
        onRefresh();
        setTimeout(() => { ind.classList.remove('av-ptr-spin'); paint(0, true); busy = false; }, 900);
      } else {
        paint(0, true);
      }
    };

    main.addEventListener('touchstart', start, { passive: true });
    main.addEventListener('touchmove', move, { passive: true });
    main.addEventListener('touchend', end, { passive: true });
    main.addEventListener('touchcancel', end, { passive: true });
    return () => {
      main.removeEventListener('touchstart', start);
      main.removeEventListener('touchmove', move);
      main.removeEventListener('touchend', end);
      main.removeEventListener('touchcancel', end);
    };
  }, [mainRef, indRef, onRefresh]);
}
